import { z } from 'zod'

/**
 * `localStorage['clearroute:lastLocation']` — last known GPS fix.
 *
 * Decisions:
 *   - D-72 fallback hierarchy step 1: bootstrap map center from a persisted
 *     fix (<7 days old) before the permission dialog resolves.
 *   - D-74: persist only when accuracy < 500m so the seed is actually useful.
 *
 * Threat mitigation T-2-LS: every read is wrapped in a Zod schema
 * (`PersistedFixSchema.safeParse`) and a `JSON.parse` try/catch so a
 * tampered or corrupted localStorage value is treated as "no fix" rather
 * than crashing the app.
 */

export const PersistedFixSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracy: z.number().nonnegative(),
  ts: z.number().int().positive(),
})

export type PersistedFix = z.infer<typeof PersistedFixSchema>

export const STORAGE_KEY = 'clearroute:lastLocation'
const TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days — D-72

/**
 * Load + validate the persisted fix. Returns `null` when:
 *   - the key is absent
 *   - `JSON.parse` throws
 *   - schema validation fails
 *   - the fix is older than TTL_MS (7 days)
 *   - `localStorage.getItem` throws (Safari lockdown / disabled)
 */
export function loadPersistedFix(): PersistedFix | null {
  let raw: string | null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
  if (raw == null) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    // Corrupted JSON (T-2-LS) — treat as "no fix".
    return null
  }

  const result = PersistedFixSchema.safeParse(parsed)
  if (!result.success) return null

  if (Date.now() - result.data.ts > TTL_MS) return null

  return result.data
}

/**
 * Write the fix when accuracy is good enough to be useful (D-74).
 * Swallows localStorage write errors (Safari private mode throws on
 * `setItem` — app keeps working, next request re-persists).
 */
export function savePersistedFix(fix: PersistedFix): void {
  if (fix.accuracy >= 500) return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fix))
  } catch {
    // ignore — see docstring.
  }
}

export function clearPersistedFix(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
